const REVIEW_TRANSITION_DURATION = 720;
const REVIEW_DOT_WIDTH = 8;
const REVIEW_PILL_WIDTH = 34;
const REVIEW_INITIALISATION_MARGIN = '400px 0px';
const LANDING_ENTRY_COMPLETE_EVENT = 'sigma:landing-entry-complete';

const carousels = document.querySelectorAll('[data-review-carousel]');

carousels.forEach((carousel) => {
  const slides = [...carousel.querySelectorAll('[data-review-slide]')];
  const controls = [...carousel.querySelectorAll('[data-review-control]')];
  const toggle = carousel.querySelector('[data-review-toggle]');
  const stage = carousel.querySelector('[data-review-stage]');
  const status = carousel.querySelector('[data-review-status]');
  const stars = carousel.querySelector('[data-review-stars]');

  if (!toggle || !stage || !status || slides.length < 2 || slides.length !== controls.length) {
    return;
  }

  const activationRoot = carousel.closest('.landing-reviews') || carousel;
  const supportsIntersectionObserver = 'IntersectionObserver' in window;
  const activationEvents = ['focusin', 'pointerdown', 'click', 'keydown'];
  let activationObserver = null;
  let visibilityObserver = null;
  let isInitialised = false;
  let isInViewport = !supportsIntersectionObserver;
  let isNearViewport = false;

  function initialise() {
    if (isInitialised) {
      return;
    }

    isInitialised = true;
    activationObserver?.disconnect();
    activationEvents.forEach((eventName) => {
      carousel.removeEventListener(eventName, initialiseFromIntent, true);
    });
    window.removeEventListener('hashchange', initialiseFromHash);
    document.removeEventListener(LANDING_ENTRY_COMPLETE_EVENT, handleLandingEntryComplete);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const duration = Number.parseInt(carousel.dataset.reviewDuration || '9000', 10);
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let remaining = duration;
    let timer = null;
    let timerStartedAt = 0;
    let statusTimer = null;
    let scrollFrame = null;
    let transitionFrame = null;
    let scrollEndTimer = null;
    let resizeFrame = null;
    let teleportTimer = null;
    let pendingTarget = null;
    let lastScrollLeft = 0;
    let scrollDirection = 1;
    let isInitialising = true;
    let isTeleporting = false;
    let isManuallyPaused = false;
    let isManualInteraction = false;
    let isPointerActive = false;

    const makeClone = (slide, index) => {
      const clone = slide.cloneNode(true);

      clone.classList.remove('is-active');
      clone.removeAttribute('data-review-slide');
      clone.dataset.reviewClone = 'true';
      clone.dataset.reviewIndex = `${index}`;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      return clone;
    };

    slides.forEach((slide, index) => {
      slide.dataset.reviewIndex = `${index}`;
    });

    const lastClone = makeClone(slides[slides.length - 1], slides.length - 1);
    const firstClone = makeClone(slides[0], 0);
    stage.prepend(lastClone);
    stage.append(firstClone);

    const railItems = [lastClone, ...slides, firstClone];
    const railAttributionItems = railItems.map((slide) => [
      ...slide.querySelectorAll('[data-review-attribution-item]'),
    ]);
    carousel.style.setProperty('--review-duration', `${duration}ms`);

    const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
    const itemLeft = (item) => item.offsetLeft;

    const clearTimer = () => {
      if (timer === null) {
        return;
      }

      window.clearTimeout(timer);
      timer = null;
    };

    const updateToggle = () => {
      if (reducedMotion.matches) {
        toggle.disabled = true;
        toggle.setAttribute('aria-label', 'Review autoplay disabled by reduced motion preference');
        return;
      }

      toggle.disabled = false;
      toggle.setAttribute(
        'aria-label',
        carousel.dataset.reviewState === 'running' ? 'Pause review autoplay' : 'Play review autoplay'
      );
    };

    const pause = ({ preserveProgress = true } = {}) => {
      if (timer !== null && preserveProgress) {
        remaining = Math.max(0, remaining - (Date.now() - timerStartedAt));
      }

      clearTimer();

      if (!preserveProgress) {
        remaining = duration;
      }

      carousel.dataset.reviewState = reducedMotion.matches ? 'reduced' : 'paused';
      updateToggle();
    };

    const shouldRun = () =>
      !reducedMotion.matches &&
      !isManuallyPaused &&
      !isPointerActive &&
      !pendingTarget &&
      isInViewport &&
      document.visibilityState === 'visible';

    const updateControls = () => {
      controls.forEach((control, index) => {
        if (index === activeIndex) {
          control.setAttribute('aria-current', 'true');
          control.tabIndex = 0;
        } else {
          control.removeAttribute('aria-current');
          control.tabIndex = -1;
        }
      });
    };

    const updateVisuals = () => {
      scrollFrame = null;
      const stageBounds = stage.getBoundingClientRect();
      const stageWidth = Math.max(1, stage.clientWidth);
      const stageCenter = stage.scrollLeft + stageWidth / 2;
      const isBackward = carousel.dataset.reviewDirection === 'backward';
      const measurements = railItems.map((slide, index) => {
        const slideCenter = itemLeft(slide) + slide.offsetWidth / 2;
        const distance = slideCenter - stageCenter;
        const focus = 1 - clamp(Math.abs(distance) / stageWidth, 0, 1);

        return {
          attributionItems: railAttributionItems[index],
          copyOffset: clamp(distance * 0.06, -18, 18),
          focus,
          slide,
        };
      });

      measurements.forEach(({ attributionItems, copyOffset, focus, slide }) => {
        slide.style.setProperty('--review-focus', focus.toFixed(4));
        slide.style.setProperty('--review-copy-x', `${copyOffset.toFixed(2)}px`);
        slide.style.setProperty('--review-copy-y', `${((1 - focus) * 7).toFixed(2)}px`);
        slide.style.setProperty('--review-copy-opacity', (0.16 + focus * 0.84).toFixed(4));
        slide.style.setProperty('--review-copy-blur', `${((1 - focus) * 2).toFixed(2)}px`);

        attributionItems.forEach((item, index) => {
          const order = isBackward ? attributionItems.length - index - 1 : index;
          const stagger = order * 0.07;
          const itemFocus = clamp((focus - stagger) / (1 - stagger), 0, 1);

          item.style.setProperty('--review-item-opacity', (0.08 + itemFocus * 0.92).toFixed(4));
          item.style.setProperty('--review-item-blur', `${((1 - itemFocus) * 3).toFixed(2)}px`);
          item.style.setProperty('--review-item-y', `${((1 - itemFocus) * 6).toFixed(2)}px`);
        });
      });

      return stageBounds.width;
    };

    const requestVisualUpdate = () => {
      if (scrollFrame !== null) {
        return;
      }

      scrollFrame = window.requestAnimationFrame(updateVisuals);
    };

    const alignTo = (item, behavior = 'auto') => {
      stage.scrollTo({ left: itemLeft(item), behavior });
    };

    const beginTeleport = () => {
      window.clearTimeout(teleportTimer);
      isTeleporting = true;
    };

    const finishTeleport = () => {
      window.clearTimeout(teleportTimer);
      teleportTimer = window.setTimeout(() => {
        isTeleporting = false;
      }, 160);
    };

    const closestItem = () =>
      railItems.reduce((closest, item) =>
        Math.abs(itemLeft(item) - stage.scrollLeft) < Math.abs(itemLeft(closest) - stage.scrollLeft)
          ? item
          : closest
      );

    const announceReview = () => {
      window.clearTimeout(statusTimer);
      status.textContent = `Review ${activeIndex + 1} of ${slides.length}`;
      statusTimer = window.setTimeout(() => {
        status.textContent = '';
      }, 1000);
    };

    const replayStars = () => {
      if (!stars) {
        return;
      }

      stars.classList.remove('is-changing');

      if (reducedMotion.matches) {
        return;
      }

      // Force a fresh animation timeline when a new review settles, including
      // rapid pager or swipe changes that happen before the previous effect ends.
      void stars.offsetWidth;
      stars.classList.add('is-changing');
    };

    const schedule = () => {
      clearTimer();

      if (!shouldRun()) {
        pause();
        return;
      }

      carousel.dataset.reviewState = 'running';
      updateToggle();
      timerStartedAt = Date.now();
      timer = window.setTimeout(() => {
        timer = null;
        goTo(activeIndex + 1, { direction: 1, behavior: 'smooth' });
      }, remaining);
    };

    const finishScroll = () => {
      if (transitionFrame !== null || isPointerActive) {
        return;
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = null;
      carousel.removeAttribute('data-review-scroll-state');

      const settledItem = closestItem();
      const nextIndex = Number.parseInt(settledItem.dataset.reviewIndex || '0', 10);
      const didChange = nextIndex !== activeIndex;
      const shouldAnnounce = pendingTarget?.announce || (isManualInteraction && didChange);

      if (settledItem.hasAttribute('data-review-clone')) {
        beginTeleport();
        alignTo(slides[nextIndex]);
        lastScrollLeft = itemLeft(slides[nextIndex]);
        window.requestAnimationFrame(() => {
          requestVisualUpdate();
        });
        finishTeleport();
      }

      activeIndex = nextIndex;
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      pendingTarget = null;
      isManualInteraction = false;
      remaining = duration;
      controls.forEach((control) => {
        control.classList.remove('is-transitioning-in', 'is-transitioning-out');
        control.querySelector('.landing-reviews__pager-segment')?.style.removeProperty('width');
      });
      updateControls();
      requestVisualUpdate();

      if (didChange) {
        replayStars();
      }

      if (shouldAnnounce) {
        announceReview();
      }

      schedule();
    };

    const queueScrollFinish = () => {
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(finishScroll, 120);
    };

    const targetItemFor = (nextIndex, direction) => {
      if (activeIndex === slides.length - 1 && nextIndex === 0 && direction > 0) {
        return firstClone;
      }

      if (activeIndex === 0 && nextIndex === slides.length - 1 && direction < 0) {
        return lastClone;
      }

      return slides[nextIndex];
    };

    const easeInOutCubic = (progress) =>
      progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;

    const animateTo = (targetItem, nextIndex) => {
      window.cancelAnimationFrame(transitionFrame);
      transitionFrame = null;

      const startLeft = stage.scrollLeft;
      const targetLeft = itemLeft(targetItem);
      const outgoingSegment = controls[activeIndex].querySelector('.landing-reviews__pager-segment');
      const incomingSegment = controls[nextIndex].querySelector('.landing-reviews__pager-segment');
      const startedAt = performance.now();

      controls.forEach((control) => control.classList.remove('is-transitioning-in', 'is-transitioning-out'));
      controls[activeIndex].classList.add('is-transitioning-out');
      controls[nextIndex].classList.add('is-transitioning-in');
      stage.classList.add('is-programmatic-transition');

      const tick = (now) => {
        const elapsed = Math.min(1, (now - startedAt) / REVIEW_TRANSITION_DURATION);
        const progress = easeInOutCubic(elapsed);

        stage.scrollLeft = startLeft + (targetLeft - startLeft) * progress;
        outgoingSegment.style.width = `${REVIEW_PILL_WIDTH - (REVIEW_PILL_WIDTH - REVIEW_DOT_WIDTH) * progress}px`;
        incomingSegment.style.width = `${REVIEW_DOT_WIDTH + (REVIEW_PILL_WIDTH - REVIEW_DOT_WIDTH) * progress}px`;

        if (elapsed < 1) {
          transitionFrame = window.requestAnimationFrame(tick);
          return;
        }

        transitionFrame = null;
        stage.scrollLeft = targetLeft;
        stage.classList.remove('is-programmatic-transition');
        finishScroll();
      };

      transitionFrame = window.requestAnimationFrame(tick);
    };

    const cancelTransition = () => {
      if (transitionFrame !== null) {
        window.cancelAnimationFrame(transitionFrame);
        transitionFrame = null;
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = null;
      stage.classList.remove('is-programmatic-transition');
      controls.forEach((control) => {
        control.classList.remove('is-transitioning-in', 'is-transitioning-out');
        control.querySelector('.landing-reviews__pager-segment')?.style.removeProperty('width');
      });
    };

    const settlePendingTransition = () => {
      if (!pendingTarget && transitionFrame === null) {
        return;
      }

      const targetIndex = pendingTarget?.index ?? activeIndex;

      cancelTransition();
      beginTeleport();
      alignTo(slides[targetIndex]);
      lastScrollLeft = itemLeft(slides[targetIndex]);
      requestVisualUpdate();
      finishTeleport();
      finishScroll();
    };

    function goTo(nextIndex, { direction = 1, behavior = 'smooth', announce = false } = {}) {
      const normalizedIndex = (nextIndex + slides.length) % slides.length;
      const resolvedBehavior = reducedMotion.matches ? 'auto' : behavior;
      const targetItem = targetItemFor(normalizedIndex, direction);

      pendingTarget = { index: normalizedIndex, direction, announce };
      scrollDirection = direction;
      carousel.dataset.reviewDirection = direction > 0 ? 'forward' : 'backward';
      carousel.dataset.reviewScrollState = 'scrolling';
      if (resolvedBehavior === 'auto' || Math.abs(itemLeft(targetItem) - stage.scrollLeft) < 1) {
        window.cancelAnimationFrame(transitionFrame);
        transitionFrame = null;
        stage.classList.remove('is-programmatic-transition');
        alignTo(targetItem);
        requestVisualUpdate();
        window.requestAnimationFrame(finishScroll);
      } else {
        animateTo(targetItem, normalizedIndex);
      }
    }

    controls.forEach((control) => {
      control.addEventListener('click', (event) => {
        const nextIndex = Number.parseInt(control.dataset.reviewControl || '0', 10);
        const direction = nextIndex >= activeIndex ? 1 : -1;

        isManualInteraction = true;
        pause({ preserveProgress: false });
        goTo(nextIndex, {
          direction,
          behavior: event.detail === 0 ? 'auto' : 'smooth',
          announce: true,
        });
      });

      control.addEventListener('keydown', (event) => {
        const currentControlIndex = controls.indexOf(control);
        let nextControlIndex = null;

        if (event.key === 'ArrowRight') {
          nextControlIndex = (currentControlIndex + 1) % controls.length;
        } else if (event.key === 'ArrowLeft') {
          nextControlIndex = (currentControlIndex - 1 + controls.length) % controls.length;
        } else if (event.key === 'Home') {
          nextControlIndex = 0;
        } else if (event.key === 'End') {
          nextControlIndex = controls.length - 1;
        }

        if (nextControlIndex === null) {
          return;
        }

        event.preventDefault();
        controls[nextControlIndex].focus();
        controls[nextControlIndex].click();
      });
    });

    toggle.addEventListener('click', () => {
      if (reducedMotion.matches) {
        return;
      }

      if (!isManuallyPaused) {
        isManuallyPaused = true;
        settlePendingTransition();
        pause();
        return;
      }

      isManuallyPaused = false;
      schedule();
    });

    stage.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) {
        return;
      }

      isPointerActive = true;
      isManualInteraction = true;
      cancelTransition();
      pendingTarget = null;
      pause({ preserveProgress: false });
    });

    const finishPointerInteraction = (event) => {
      if (!event.isPrimary || !isPointerActive) {
        return;
      }

      isPointerActive = false;
      queueScrollFinish();
    };

    window.addEventListener('pointerup', finishPointerInteraction);
    window.addEventListener('pointercancel', finishPointerInteraction);

    stage.addEventListener(
      'scroll',
      () => {
        const delta = stage.scrollLeft - lastScrollLeft;

        if (Math.abs(delta) > 0.5) {
          scrollDirection = delta > 0 ? 1 : -1;
          carousel.dataset.reviewDirection = scrollDirection > 0 ? 'forward' : 'backward';

          if (!isInitialising && !isTeleporting && !pendingTarget) {
            isManualInteraction = true;
            pause({ preserveProgress: false });
          }
        }

        lastScrollLeft = stage.scrollLeft;
        carousel.dataset.reviewScrollState = 'scrolling';
        requestVisualUpdate();
        queueScrollFinish();
      },
      { passive: true }
    );

    stage.addEventListener('scrollend', finishScroll);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        isPointerActive = false;
        settlePendingTransition();
        pause();
      } else {
        schedule();
      }
    });

    reducedMotion.addEventListener?.('change', () => {
      stars?.classList.remove('is-changing');
      settlePendingTransition();
      remaining = duration;
      beginTeleport();
      alignTo(slides[activeIndex]);
      requestVisualUpdate();
      finishTeleport();
      schedule();
    });

    const alignAfterResize = () => {
      if (resizeFrame !== null) {
        return;
      }

      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        settlePendingTransition();
        beginTeleport();
        alignTo(slides[activeIndex]);
        lastScrollLeft = stage.scrollLeft;
        updateVisuals();
        finishTeleport();
      });
    };

    if (supportsIntersectionObserver) {
      visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        isInViewport = entry.isIntersecting && entry.intersectionRatio > 0;

        if (isInViewport) {
          schedule();
        } else {
          pause();
        }
      });
      visibilityObserver.observe(carousel);
    } else {
      isInViewport = true;
    }

    updateControls();
    updateToggle();
    beginTeleport();
    alignTo(slides[activeIndex]);
    window.requestAnimationFrame(() => {
      if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = null;
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = null;
      carousel.removeAttribute('data-review-scroll-state');
      lastScrollLeft = stage.scrollLeft;
      const initialStageWidth = updateVisuals();
      isInitialising = false;
      finishTeleport();

      if ('ResizeObserver' in window) {
        let observedStageWidth = Math.round(initialStageWidth);
        const resizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          const borderBoxSize = Array.isArray(entry?.borderBoxSize)
            ? entry.borderBoxSize[0]
            : entry?.borderBoxSize;
          const nextStageWidth = borderBoxSize?.inlineSize ?? stage.getBoundingClientRect().width;

          if (typeof nextStageWidth !== 'number') {
            return;
          }

          const roundedStageWidth = Math.round(nextStageWidth);

          if (roundedStageWidth === observedStageWidth) {
            return;
          }

          observedStageWidth = roundedStageWidth;
          alignAfterResize();
        });
        resizeObserver.observe(stage);
      } else {
        window.addEventListener('resize', alignAfterResize);
      }

      schedule();
    });
  }

  const hashTargetsReviews = () => {
    if (!window.location.hash) {
      return false;
    }

    let targetId;

    try {
      targetId = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      targetId = window.location.hash.slice(1);
    }

    const target = document.getElementById(targetId);
    return target === activationRoot || (target !== null && activationRoot.contains(target));
  };

  const initialiseFromIntent = () => initialise();
  const initialiseFromHash = () => {
    if (hashTargetsReviews()) {
      initialise();
    }
  };
  const handleLandingEntryComplete = () => {
    landingEntryIsComplete = true;

    if (isNearViewport) {
      initialise();
    }
  };
  const landingEntryState = document.documentElement.dataset.landingEntryState;
  let landingEntryIsComplete = !['pending', 'running'].includes(landingEntryState);

  activationEvents.forEach((eventName) => {
    carousel.addEventListener(eventName, initialiseFromIntent, true);
  });
  window.addEventListener('hashchange', initialiseFromHash);
  document.addEventListener(LANDING_ENTRY_COMPLETE_EVENT, handleLandingEntryComplete);

  if (
    hashTargetsReviews() ||
    (document.activeElement instanceof Element && carousel.contains(document.activeElement))
  ) {
    initialise();
    return;
  }

  if (!supportsIntersectionObserver) {
    initialise();
    return;
  }

  activationObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const bounds = entry.boundingClientRect;
      const isVisible =
        bounds.bottom > 0 &&
        bounds.right > 0 &&
        bounds.top < window.innerHeight &&
        bounds.left < window.innerWidth;

      isNearViewport = entry.isIntersecting;

      if (isVisible) {
        isInViewport = true;
      }

      if (isVisible || (isNearViewport && landingEntryIsComplete)) {
        initialise();
      }
    },
    { rootMargin: REVIEW_INITIALISATION_MARGIN }
  );
  activationObserver.observe(carousel);
});
